import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 需要自动翻译的语言（非中英文）
const AUTO_TRANSLATE_LANGUAGES = ['ru', 'uk', 'fr', 'es', 'pt', 'ja', 'ko', 'ar', 'uz'];

// 不翻译的品牌词汇
const PROTECTED_TERMS = ['ArtCanvas', 'Organic Living', 'GOTS'];

// 全局翻译缓存
const globalTranslationCache = new Map();
const pendingRequests = new Map();

// Context for translations
const TranslationContext = createContext(null);

export const useTranslations = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
};

/**
 * 保护品牌词汇
 */
const protectTerms = (text) => {
  if (!text || typeof text !== 'string') return { text, placeholders: {} };
  let result = text;
  const placeholders = {};
  PROTECTED_TERMS.forEach((term, idx) => {
    const ph = `__P${idx}__`;
    if (result.includes(term)) {
      placeholders[ph] = term;
      result = result.split(term).join(ph);
    }
  });
  return { text: result, placeholders };
};

/**
 * 恢复品牌词汇
 */
const restoreTerms = (text, placeholders) => {
  if (!text) return text;
  let result = text;
  Object.entries(placeholders).forEach(([ph, term]) => {
    result = result.split(ph).join(term);
  });
  return result;
};

/**
 * 单文本翻译API
 */
const translateSingle = async (text, targetLang) => {
  if (!text) return text;
  
  const cacheKey = `${targetLang}:${text}`;
  if (globalTranslationCache.has(cacheKey)) {
    return globalTranslationCache.get(cacheKey);
  }
  
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }
  
  const promise = (async () => {
    try {
      const { text: pText, placeholders } = protectTerms(text);
      const res = await fetch(`${API}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pText, target_language: targetLang }),
      });
      const data = await res.json();
      const translated = restoreTerms(data.translated_text || text, placeholders);
      globalTranslationCache.set(cacheKey, translated);
      return translated;
    } catch (e) {
      return text;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();
  
  pendingRequests.set(cacheKey, promise);
  return promise;
};

/**
 * 批量翻译API
 */
const translateBatch = async (texts, targetLang) => {
  const results = {};
  const toTranslate = {};
  const protectedMap = {};
  
  for (const [key, text] of Object.entries(texts)) {
    if (!text) { results[key] = text; continue; }
    const cacheKey = `${targetLang}:${text}`;
    if (globalTranslationCache.has(cacheKey)) {
      results[key] = globalTranslationCache.get(cacheKey);
    } else {
      const { text: pText, placeholders } = protectTerms(text);
      toTranslate[key] = pText;
      protectedMap[key] = { original: text, placeholders };
    }
  }
  
  if (Object.keys(toTranslate).length > 0) {
    try {
      const res = await fetch(`${API}/translate/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texts: toTranslate, target_language: targetLang }),
      });
      const data = await res.json();
      if (data.translations) {
        for (const [key, translated] of Object.entries(data.translations)) {
          const restored = restoreTerms(translated, protectedMap[key]?.placeholders || {});
          results[key] = restored;
          const orig = protectedMap[key]?.original;
          if (orig) globalTranslationCache.set(`${targetLang}:${orig}`, restored);
        }
      }
    } catch (e) {
      for (const key of Object.keys(toTranslate)) {
        results[key] = protectedMap[key]?.original || '';
      }
    }
  }
  
  return results;
};

export const TranslationProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [categoryTranslations, setCategoryTranslations] = useState([]);
  const [tagTranslations, setTagTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoTranslations, setAutoTranslations] = useState({});
  const [isAutoTranslating, setIsAutoTranslating] = useState(false);
  
  const currentLang = i18n.language;
  const needsAutoTranslation = AUTO_TRANSLATE_LANGUAGES.includes(currentLang);
  
  // 翻译队列
  const translationQueue = useRef(new Map());
  const queueTimer = useRef(null);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const [categoriesRes, tagsRes] = await Promise.all([
          axios.get(`${API}/categories/translations`),
          axios.get(`${API}/tags/translations`)
        ]);
        setCategoryTranslations(categoriesRes.data);
        setTagTranslations(tagsRes.data);
      } catch (err) {
        console.error('Failed to fetch translations', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTranslations();
  }, []);
  
  // 语言切换时清空自动翻译缓存
  useEffect(() => {
    setAutoTranslations({});
    translationQueue.current.clear();
  }, [currentLang]);
  
  // 处理翻译队列
  const processQueue = useCallback(async () => {
    if (translationQueue.current.size === 0 || !needsAutoTranslation) return;
    
    const texts = {};
    translationQueue.current.forEach((text, key) => { texts[key] = text; });
    translationQueue.current.clear();
    
    setIsAutoTranslating(true);
    try {
      const translated = await translateBatch(texts, currentLang);
      setAutoTranslations(prev => ({ ...prev, ...translated }));
    } finally {
      setIsAutoTranslating(false);
    }
  }, [currentLang, needsAutoTranslation]);
  
  // 自动翻译单个文本（加入队列）
  const autoTranslate = useCallback((text) => {
    if (!text || typeof text !== 'string' || !needsAutoTranslation) return text;
    
    // 检查缓存
    const cacheKey = `${currentLang}:${text}`;
    if (globalTranslationCache.has(cacheKey)) {
      return globalTranslationCache.get(cacheKey);
    }
    
    // 检查本地状态
    if (autoTranslations[text]) {
      return autoTranslations[text];
    }
    
    // 加入队列
    if (!translationQueue.current.has(text)) {
      translationQueue.current.set(text, text);
      if (queueTimer.current) clearTimeout(queueTimer.current);
      queueTimer.current = setTimeout(processQueue, 50);
    }
    
    return text;
  }, [currentLang, needsAutoTranslation, autoTranslations, processQueue]);
  
  // 立即翻译（异步）
  const autoTranslateAsync = useCallback(async (text) => {
    if (!text || !needsAutoTranslation) return text;
    return translateSingle(text, currentLang);
  }, [currentLang, needsAutoTranslation]);

  // 获取分类翻译
  const getCategoryTranslation = useCallback((key, lang, type = 'product') => {
    const translation = categoryTranslations.find(
      t => t.key === key && t.type === type
    );
    if (translation) {
      const result = translation[lang] || translation.zh || key;
      // 对于非中英语言，自动翻译
      if (AUTO_TRANSLATE_LANGUAGES.includes(lang)) {
        return autoTranslate(result);
      }
      return result;
    }
    return key;
  }, [categoryTranslations, autoTranslate]);

  // 获取标签翻译
  const getTagTranslation = useCallback((key, lang) => {
    const translation = tagTranslations.find(t => t.key === key);
    if (translation) {
      const result = translation[lang] || translation.zh || key;
      if (AUTO_TRANSLATE_LANGUAGES.includes(lang)) {
        return autoTranslate(result);
      }
      return result;
    }
    return key;
  }, [tagTranslations, autoTranslate]);

  // 获取某类型的所有分类（已翻译）
  const getCategories = useCallback((type, lang) => {
    return categoryTranslations
      .filter(t => t.type === type)
      .map(t => {
        let label = t[lang] || t.zh || t.key;
        if (AUTO_TRANSLATE_LANGUAGES.includes(lang)) {
          label = autoTranslate(label);
        }
        return { key: t.key, label };
      });
  }, [categoryTranslations, autoTranslate]);

  // 批量翻译标签
  const translateTags = useCallback((tags, lang) => {
    if (!tags || !Array.isArray(tags)) return [];
    return tags.map(tag => getTagTranslation(tag, lang));
  }, [getTagTranslation]);

  return (
    <TranslationContext.Provider value={{
      categoryTranslations,
      tagTranslations,
      getCategoryTranslation,
      getTagTranslation,
      getCategories,
      translateTags,
      loading,
      // 新增的自动翻译功能
      autoTranslate,
      autoTranslateAsync,
      isAutoTranslating,
      needsAutoTranslation,
      currentLang,
    }}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;
